import { useCallback, useEffect, useState } from 'react';
import { useDevices, useInputSettings } from '@daily-co/daily-react';
import {
  Mic as MicIcon,
  RadioButtonChecked,
  RadioButtonUnchecked,
  VolumeUp as SpeakerIcon,
} from '@mui/icons-material';
import {
  Box,
  ButtonBase,
  Divider,
  Stack,
  Switch,
  Typography,
} from '@mui/material';
import type { AudioInputDeviceService, NormalizedDevice } from '@services/audio';
import { normalizedToMediaDeviceInfo } from '@services/audio';
import { useEventTracking } from '@services/statsig';
import { useAppDispatch, useAppSelector } from '@store/hooks';

import {
  selectNoiseCancellationEnabled,
  setNoiseCancellation,
  setSelectedSpeaker,
} from '../store';
import type { TelehealthSession } from '../types';

interface MicDropdownProps {
  audioInputDeviceService: AudioInputDeviceService | null;
  isRecording: boolean;
  session: TelehealthSession;
  setSelectedMicrophone: (mic: MediaDeviceInfo) => void;
  onClose: () => void;
}

export function MicDropdown({
  audioInputDeviceService,
  isRecording,
  session,
  setSelectedMicrophone,
  onClose,
}: MicDropdownProps) {
  const dispatch = useAppDispatch();
  const { telehealth } = useEventTracking();
  const { speakers, setSpeaker, refreshDevices } = useDevices();
  const { updateInputSettings } = useInputSettings();
  const noiseCancellationEnabled = useAppSelector(selectNoiseCancellationEnabled);

  const [micDevices, setMicDevices] = useState<NormalizedDevice[]>([]);
  const [selectedMicId, setSelectedMicId] = useState<string>('');
  const [noiseSupported, setNoiseSupported] = useState(true);

  // Load mic devices
  useEffect(() => {
    if (!audioInputDeviceService) return;

    const refresh = () => {
      const devices = audioInputDeviceService.getDevices();
      setMicDevices(devices);

      const active = audioInputDeviceService.getActiveStream();
      if (active) {
        setSelectedMicId(active.device.internalId);
      }
    };

    refresh();
    audioInputDeviceService.on('devicesChanged', refresh);
    return () => {
      audioInputDeviceService.off('devicesChanged', refresh);
    };
  }, [audioInputDeviceService]);

  // Hot-plug
  useEffect(() => {
    const handler = () => {
      if (audioInputDeviceService) {
        setMicDevices(audioInputDeviceService.getDevices());
      }
    };
    navigator.mediaDevices.addEventListener('devicechange', handler);
    return () => navigator.mediaDevices.removeEventListener('devicechange', handler);
  }, [audioInputDeviceService]);

  // Refresh Daily device list on mount so speakers are populated immediately
  useEffect(() => {
    refreshDevices();
  }, [refreshDevices]);

  // Apply noise cancellation from Redux on mount
  useEffect(() => {
    if (noiseCancellationEnabled) {
      updateInputSettings?.({
        audio: { processor: { type: 'noise-cancellation' } },
      })?.catch(() => setNoiseSupported(false));
    }
    // Only run on mount
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleMicSelect = useCallback(
    (device: NormalizedDevice) => {
      try {
        setSelectedMicrophone(normalizedToMediaDeviceInfo(device));
        setSelectedMicId(device.internalId);
        telehealth.deviceSwitched({
          appointment_id: session.appointmentId,
          device_type: 'mic',
          during_recording: isRecording,
        });
        onClose();
      } catch (err) {
        telehealth.deviceSwitchFailed({
          appointment_id: session.appointmentId,
          device_type: 'mic',
          error_code: err instanceof Error ? err.name : 'unknown',
        });
      }
    },
    [setSelectedMicrophone, isRecording, session.appointmentId, telehealth, onClose],
  );

  const handleSpeakerSelect = useCallback(
    async (deviceId: string) => {
      try {
        await setSpeaker(deviceId);
        dispatch(setSelectedSpeaker(deviceId));
        telehealth.deviceSwitched({
          appointment_id: session.appointmentId,
          device_type: 'speaker',
          during_recording: isRecording,
        });
        onClose();
      } catch (err) {
        telehealth.deviceSwitchFailed({
          appointment_id: session.appointmentId,
          device_type: 'speaker',
          error_code: err instanceof Error ? err.name : 'unknown',
        });
      }
    },
    [setSpeaker, dispatch, isRecording, session.appointmentId, telehealth, onClose],
  );

  const handleNoiseToggle = useCallback(
    async (enabled: boolean) => {
      try {
        await updateInputSettings?.({
          audio: {
            processor: { type: enabled ? 'noise-cancellation' : 'none' },
          },
        });
        dispatch(setNoiseCancellation(enabled));
        telehealth.noiseCancellationToggled({
          appointment_id: session.appointmentId,
          enabled,
        });
      } catch {
        setNoiseSupported(false);
      }
    },
    [updateInputSettings, dispatch, session.appointmentId, telehealth],
  );

  return (
    <Stack sx={{ py: 1 }}>
      {/* Microphone section */}
      <Stack direction="row" spacing={1} sx={{alignItems: "center",  px: 2, py: 1 }}>
        <MicIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
        <Typography variant="subtitle2">Microphone</Typography>
      </Stack>
      {micDevices.map((d) => (
        <ButtonBase
          key={d.internalId}
          onClick={() => handleMicSelect(d)}
          sx={{
            display: 'flex',
            alignItems: 'center',
            width: '100%',
            px: 2,
            py: 0.75,
            gap: 1,
            justifyContent: 'flex-start',
            '&:hover': { bgcolor: 'action.hover' },
          }}
        >
          {d.internalId === selectedMicId ? (
            <RadioButtonChecked sx={{ fontSize: 18, color: 'primary.main' }} />
          ) : (
            <RadioButtonUnchecked sx={{ fontSize: 18, color: 'text.disabled' }} />
          )}
          <Typography variant="body2" noWrap>
            {d.displayLabel}
          </Typography>
        </ButtonBase>
      ))}

      <Divider sx={{ my: 1 }} />

      {/* Speaker section */}
      <Stack direction="row" spacing={1} sx={{alignItems: "center",  px: 2, py: 1 }}>
        <SpeakerIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
        <Typography variant="subtitle2">Speakers</Typography>
      </Stack>
      {speakers.map((s) => (
        <ButtonBase
          key={s.device.deviceId}
          onClick={() => handleSpeakerSelect(s.device.deviceId)}
          sx={{
            display: 'flex',
            alignItems: 'center',
            width: '100%',
            px: 2,
            py: 0.75,
            gap: 1,
            justifyContent: 'flex-start',
            '&:hover': { bgcolor: 'action.hover' },
          }}
        >
          {s.selected ? (
            <RadioButtonChecked sx={{ fontSize: 18, color: 'primary.main' }} />
          ) : (
            <RadioButtonUnchecked sx={{ fontSize: 18, color: 'text.disabled' }} />
          )}
          <Typography variant="body2" noWrap>
            {s.device.label || `Speaker ${s.device.deviceId.slice(0, 8)}`}
          </Typography>
        </ButtonBase>
      ))}

      {/* Noise cancellation toggle */}
      {noiseSupported && (
        <>
          <Divider sx={{ my: 1 }} />
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              px: 2,
              py: 0.5,
            }}
          >
            <Typography variant="body2">Reduce mic noise</Typography>
            <Switch
              size="small"
              checked={noiseCancellationEnabled}
              onChange={(_, checked) => handleNoiseToggle(checked)}
            />
          </Box>
        </>
      )}
    </Stack>
  );
}
